// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import React, { RefObject, memo, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Icons } from 'greener-ui-library';
import { v4 as uuidv4 } from 'uuid';
import { MenuOption, MessageType, OperatorMessage } from '../operatorChat.types';

const CommandSuggestions = ({
  filteredMenu,
  selectedIndex,
  setValue,
  onMenuCommandClick,
}: {
  filteredMenu: MenuOption[];
  selectedIndex: number | null;
  setValue: (name: 'chatInput', value: string) => void;
  onMenuCommandClick: (command: string) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedIndex === null || !containerRef.current) {
      return;
    }
    const item = containerRef.current.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        width: '100%',
        maxHeight: 200,
        overflowY: 'auto',
        background: (theme) => theme.palette.background.paper,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 0.5,
        zIndex: (theme) => theme.zIndex.tooltip,
        boxShadow: 2,
      }}
    >
      {filteredMenu.map((menuItem, index) => (
        <Box
          key={menuItem.text}
          sx={{
            px: 2,
            py: 0.5,
            cursor: 'pointer',
            backgroundColor: selectedIndex === index ? 'action.selected' : 'transparent',
            '&:hover': { backgroundColor: selectedIndex === index ? 'action.selected' : 'action.hover' },
          }}
          onClick={() => {
            setValue('chatInput', '');
            onMenuCommandClick(menuItem.text);
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {menuItem.text}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {menuItem.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Isolated chat input area. Owns its own form state so that keystrokes only
 * re-render this subtree, not the entire OperatorChat (which includes the
 * potentially large message list).
 */
export const ChatInputArea = memo(
  ({
    unitId,
    sendMessage,
    menu,
    onMenuCommandClick,
    inputRef,
  }: {
    unitId: string;
    sendMessage: (message: OperatorMessage) => void;
    menu: MenuOption[];
    onMenuCommandClick: (command: string) => void;
    inputRef?: RefObject<HTMLInputElement>;
  }) => {
    const { t } = useTranslation();
    const { control, handleSubmit, reset, setValue } = useForm({
      defaultValues: { chatInput: '' },
    });
    const chatInputValue = useWatch({ control, name: 'chatInput' }) ?? '';
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number | null>(null);

    const filteredMenu =
      chatInputValue.startsWith('/') && menu.length > 0
        ? menu.filter((menuItem) => menuItem.text.toLowerCase().startsWith(chatInputValue.toLowerCase()))
        : [];

    useEffect(() => {
      setSelectedSuggestionIndex(null);
    }, [chatInputValue]);

    useEffect(() => {
      reset({ chatInput: '' });
    }, [unitId, reset]);

    const onSubmit = ({ chatInput }: { chatInput: string }) => {
      const words = chatInput.trim().split(/\s+/);
      const isCommand = words.length === 1 && words[0].startsWith('/');

      sendMessage({
        uuid: uuidv4(),
        message_type: isCommand ? MessageType.COMMAND : MessageType.INPUT,
        text: chatInput,
      });
      reset({ chatInput: '' });
      inputRef?.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (filteredMenu.length === 0) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev === null ? 0 : Math.min(prev + 1, filteredMenu.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev === null ? filteredMenu.length - 1 : Math.max(prev - 1, 0)));
      } else if (e.key === 'Enter' && selectedSuggestionIndex !== null) {
        e.preventDefault();
        setValue('chatInput', '');
        onMenuCommandClick(filteredMenu[selectedSuggestionIndex].text);
      }
    };

    return (
      <Box
        sx={{
          background: (theme) => theme.palette.background.paper,
          borderTop: '1px solid',
          borderColor: 'divider',
          py: 2,
          px: 2,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="row" gap={1}>
            <Box sx={{ position: 'relative', flexGrow: 1 }}>
              <TextField
                data-testid="chat-input"
                inputRef={inputRef}
                value={chatInputValue}
                onChange={(e) => setValue('chatInput', e.target.value)}
                fullWidth
                placeholder={t('unitControl:chat.inputPlaceholder')}
                variant="outlined"
                size="small"
                autoComplete="off"
                onKeyDown={handleKeyDown}
                InputProps={{
                  endAdornment: menu.length ? (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 0, width: '36px', mr: '-9px', ml: 0.5, fontWeight: 700, fontSize: 20 }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setValue('chatInput', '/');
                        inputRef?.current?.focus();
                      }}
                    >
                      /
                    </Button>
                  ) : null,
                }}
              />
              {filteredMenu.length > 0 && (
                <CommandSuggestions
                  filteredMenu={filteredMenu}
                  selectedIndex={selectedSuggestionIndex}
                  setValue={setValue}
                  onMenuCommandClick={onMenuCommandClick}
                />
              )}
            </Box>
            <Button
              data-testid="send-button"
              type="submit"
              sx={{ minWidth: 40, width: 40, pr: 0.5, color: 'primary.main' }}
              disabled={chatInputValue.trim() === ''}
            >
              <Icons.Send />
            </Button>
          </Stack>
        </form>
      </Box>
    );
  }
);

ChatInputArea.displayName = 'ChatInputArea';
